/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2026 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 18-08-2026 11:18:04
 * Autor     : Diego Brock
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpUtil } from '../utils/http-util';
import { StepUpTokenStore } from './step-up-token.store';
import {
  INSUFFICIENT_USER_AUTHENTICATION,
  STEP_UP_WRONG_PASSWORD,
  StepUpHttpError,
  isInsufficientAuthError,
} from './step-up-errors';

/**
 * Cliente de reautenticação: POST {apibase}/auth/step-up
 * e grava o access_token one-shot para a próxima chamada em kc-auth.
 */
@Injectable({ providedIn: 'root' })
export class StepUpAuthService {
  constructor(
    private http: HttpClient,
    private httpUtil: HttpUtil,
    private stepUpStore: StepUpTokenStore
  ) {}

  /**
   * Valida a senha via API de step-up e armazena o token para o próximo envio.
   *
   * @param password senha do usuário autenticado
   */
  async stepUp(password: string): Promise<void> {
    const base = (environment.apibase || '').replace(/\/$/, '');
    const url = base + '/auth/step-up';
    const headers = this.httpUtil.buildHeadersGateway({ consumeStepUp: false });

    try {
      const data = await this.http.post<any>(url, { password }, { headers }).toPromise();
      const accessToken = data && (data.accessToken || data.access_token);
      if (!accessToken) {
        throw new StepUpHttpError('UNKNOWN', 'Resposta step-up sem access_token');
      }
      this.stepUpStore.set(accessToken);
    } catch (err) {
      throw this.mapError(err);
    }
  }

  /**
   * Converte erro HTTP do step-up no código/mensagem usados no admin.
   *
   * @param err erro original
   * @returns StepUpHttpError
   */
  private mapError(err: any): StepUpHttpError {
    if (err instanceof StepUpHttpError) {
      return err;
    }
    const httpErr = err as HttpErrorResponse;
    const status = httpErr && httpErr.status;
    const body = httpErr && httpErr.error;

    if (status === 401) {
      return new StepUpHttpError(
        STEP_UP_WRONG_PASSWORD,
        'Senha incorreta. Tente novamente.',
        status
      );
    }
    if (isInsufficientAuthError(httpErr)) {
      return new StepUpHttpError(
        INSUFFICIENT_USER_AUTHENTICATION,
        'Sua sessão expirou. Digite sua senha novamente para continuar.',
        status
      );
    }
    if (status === 502 || status === 503) {
      return new StepUpHttpError(
        'SERVICE_UNAVAILABLE',
        'Serviço temporariamente indisponível. Tente novamente em instantes.',
        status
      );
    }
    const message =
      (body && body.message) ||
      (httpErr && httpErr.message) ||
      'Erro ao validar senha.';
    return new StepUpHttpError('UNKNOWN', message, status);
  }
}
